import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";
import { db, storage } from "../Components/Firebase"; // Adjust path as needed
import AdminHead from "./AdminHead";
import '../Styles/Admin/EditCategory.css';

function EditCategory() {
  const { CID } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    info: "",
    images: [],
    videos: []
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });

  useEffect(() => {
    fetchItems();
  }, [CID]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const itemsCollection = collection(db, "Catalog", CID, "Item");
      const querySnapshot = await getDocs(itemsCollection);
      const itemsData = [];
      querySnapshot.forEach((doc) => {
        itemsData.push({ id: doc.id, ...doc.data() });
      });
      setItems(itemsData);
    } catch (error) {
      console.error("Error fetching items:", error);
      alert("שגיאה בטעינת הפריטים");
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (files, folder) => {
    const uploadedUrls = [];
    const filesArray = Array.from(files);

    for (let i = 0; i < filesArray.length; i++) {
      const file = filesArray[i];
      try {
        setUploadProgress({
          current: i + 1,
          total: filesArray.length,
          fileName: file.name
        });

        const fileRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        uploadedUrls.push(downloadURL);
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        alert(`שגיאה בהעלאת הקובץ: ${file.name}`);
      }
    }

    setUploadProgress({ current: 0, total: 0, fileName: '' });
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("נא להזין שם לפריט");
      return;
    }

    try {
      setUploading(true);

      const imageUrls = formData.images.length > 0
        ? await uploadFiles(formData.images, `Catalog/${CID}/images`)
        : [];

      const videoUrls = formData.videos.length > 0
        ? await uploadFiles(formData.videos, `Catalog/${CID}/videos`)
        : [];

      const itemData = {
        name: formData.name,
        info: formData.info,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingItem) {
        const existingImages = editingItem.images || [];
        const existingVideos = editingItem.videos || [];
        itemData.images = [...existingImages, ...imageUrls];
        itemData.videos = [...existingVideos, ...videoUrls];

        await updateDoc(doc(db, "Catalog", CID, "Item", editingItem.id), itemData);
        alert("הפריט עודכן בהצלחה");
      } else {
        itemData.images = imageUrls;
        itemData.videos = videoUrls;

        await addDoc(collection(db, "Catalog", CID, "Item"), itemData);
        alert("הפריט נוסף בהצלחה");
      }

      setFormData({ name: "", info: "", images: [], videos: [] });
      setShowAddForm(false);
      setEditingItem(null);
      fetchItems();
    } catch (error) {
      console.error("Error saving item:", error);
      alert("שגיאה בשמירת הפריט");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק פריט זה?")) return;

    try {
      const deletePromises = [];

      (item.images || []).forEach(url => {
        const fileRef = ref(storage, url);
        deletePromises.push(deleteObject(fileRef).catch(() => {}));
      });

      (item.videos || []).forEach(url => {
        const fileRef = ref(storage, url);
        deletePromises.push(deleteObject(fileRef).catch(() => {}));
      });

      await Promise.all(deletePromises);
      await deleteDoc(doc(db, "Catalog", CID, "Item", item.id));
      alert("הפריט נמחק בהצלחה");
      fetchItems();
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("שגיאה במחיקת הפריט");
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      info: item.info || "",
      images: [],
      videos: []
    });
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setFormData({ name: "", info: "", images: [], videos: [] });
    setShowAddForm(false);
  };

  const removeExistingMedia = async (item, mediaUrl, mediaType) => {
    try {
      const updatedMedia = item[mediaType].filter(url => url !== mediaUrl);
      await updateDoc(doc(db, "Catalog", CID, "Item", item.id), {
        [mediaType]: updatedMedia,
        updatedAt: new Date()
      });

      const fileRef = ref(storage, mediaUrl);
      await deleteObject(fileRef).catch(() => {});
      fetchItems();
    } catch (error) {
      console.error(`Error removing ${mediaType}:`, error);
      alert(`שגיאה בהסרת ${mediaType === 'images' ? 'תמונה' : 'סרטון'}`);
    }
  };

  if (loading) {
    return (
      <>
        <AdminHead />
        <div className="edit-category-container">
          <div className="loading">טוען...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminHead />
      <div className="edit-category-container">
        <div className="category-header">
          <h1>עריכת קטגוריה</h1>
          <button className="add-item-btn" onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? "ביטול" : "הוסף פריט חדש"}
          </button>
        </div>

        {showAddForm && (
          <div className="add-item-form">
            <h2>{editingItem ? "עריכת פריט" : "הוספת פריט חדש"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>שם הפריט *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="הזן שם הפריט"
                />
              </div>

              <div className="form-group">
                <label>מידע נוסף</label>
                <textarea
                  value={formData.info}
                  onChange={(e) => setFormData({ ...formData, info: e.target.value })}
                  placeholder="הזן מידע נוסף על הפריט"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>תמונות</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setFormData(prev => ({
                      ...prev,
                      images: [...(prev.images || []), ...newFiles]
                    }));
                  }}
                />
              </div>

              {formData.images.length > 0 && (
                <div className="new-media-preview">
                  <h4>תמונות שנבחרו:</h4>
                  <div className="media-grid">
                    {formData.images.map((file, index) => (
                      <div key={index} className="media-item">
                        <img src={URL.createObjectURL(file)} alt={`preview-${index}`} />
                        <button
                          type="button"
                          className="remove-media-btn"
                          onClick={() => {
                            const updated = [...formData.images];
                            updated.splice(index, 1);
                            setFormData(prev => ({ ...prev, images: updated }));
                          }}
                        >
                          הסר
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>סרטונים</label>
                <input
                  type="file"
                  multiple
                  accept="video/*"
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setFormData(prev => ({
                      ...prev,
                      videos: [...(prev.videos || []), ...newFiles]
                    }));
                  }}
                />
              </div>

              {editingItem && (
                <div className="existing-media">
                  {editingItem.images?.length > 0 && (
                    <div className="media-section">
                      <h4>תמונות קיימות:</h4>
                      <div className="media-grid">
                        {editingItem.images.map((url, index) => (
                          <div key={index} className="media-item">
                            <img src={url} alt={`תמונה ${index + 1}`} />
                            <button
                              type="button"
                              className="remove-media-btn"
                              onClick={() => removeExistingMedia(editingItem, url, 'images')}
                            >
                              הסר
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {editingItem.videos?.length > 0 && (
                    <div className="media-section">
                      <h4>סרטונים קיימים:</h4>
                      <div className="media-grid">
                        {editingItem.videos.map((url, index) => (
                          <div key={index} className="media-item">
                            <video controls>
                              <source src={url} />
                            </video>
                            <button
                              type="button"
                              className="remove-media-btn"
                              onClick={() => removeExistingMedia(editingItem, url, 'videos')}
                            >
                              הסר
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-actions">
                {uploading && uploadProgress.total > 0 && (
                  <div className="upload-progress">
                    <p>מעלה קובץ {uploadProgress.current} מתוך {uploadProgress.total}</p>
                    <p className="current-file">{uploadProgress.fileName}</p>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                <button type="submit" disabled={uploading}>
                  {uploading ? "מעלה קבצים..." : (editingItem ? "עדכן פריט" : "הוסף פריט")}
                </button>
                <button type="button" onClick={cancelEdit} disabled={uploading}>ביטול</button>
              </div>
            </form>
          </div>
        )}

        <div className="items-grid">
          {items.length === 0 ? (
            <div className="no-items">אין פריטים בקטגוריה זו</div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="item-card">
                <div className="item-header">
                  <h3>{item.name}</h3>
                  <div className="item-actions">
                    <button onClick={() => startEdit(item)} className="edit-btn">ערוך</button>
                    <button onClick={() => handleDelete(item)} className="delete-btn">מחק</button>
                  </div>
                </div>

                {item.info && <p className="item-info">{item.info}</p>}

                {item.images?.length > 0 && (
                  <div className="item-media">
                    <h4>תמונות:</h4>
                    <div className="images-grid">
                      {item.images.map((url, index) => (
                        <img key={index} src={url} alt={`תמונה ${index + 1}`} onClick={() => window.open(url, '_blank')} />
                      ))}
                    </div>
                  </div>
                )}

                {item.videos?.length > 0 && (
                  <div className="item-media">
                    <h4>סרטונים:</h4>
                    <div className="videos-grid">
                      {item.videos.map((url, index) => (
                        <video key={index} controls>
                          <source src={url} />
                          הדפדפן שלך לא תומך בהצגת סרטונים
                        </video>
                      ))}
                    </div>
                  </div>
                )}

                <div className="item-footer">
                  <small>נוצר: {new Date(item.createdAt?.seconds * 1000).toLocaleDateString('he-IL')}</small>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default EditCategory;
